import React, { FC, useMemo, useState } from 'react';

import { DataTable, type DataTableSelectCellOption } from '@column/ui';

import { formatCounterpartyLabel, useCounterpartyOptions } from '~/components/fields/CounterpartyDropdown';
import { useDebounce } from '~/hooks';
import { highlightMatch, renderMatchFragment } from '~/lib/components/selectors';
import { type HighlightSuffixCandidate, getHighlightedSuffix } from '~/lib/search/highlight';

interface CounterpartyCellProps {
  isEditable?: boolean;
  value?: string;
  onChange?: ({ description, id }: { description: string; id: string }) => void;
}

const HIDDEN_MATCH_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'BENEFICIARY_EMAIL', label: 'EMAIL' },
  { field: 'BENEFICIARY_PHONE', label: 'PHONE' },
  { field: 'LOCAL_ACCOUNT_NUMBER', label: 'LOCAL ACCT' },
  { field: 'LOCAL_BANK_CODE', label: 'LOCAL BANK' },
  { field: 'COUNTERPARTY_ID', label: 'ID' },
];

export const CounterpartyCell: FC<CounterpartyCellProps> = ({ isEditable, value, onChange }) => {
  const {
    options: dropdownOptions,
    hiddenOptions,
    search,
    handleScrollEnd,
    counterparties,
  } = useCounterpartyOptions({ value });
  const [query, setQuery] = useState('');

  const [handleSearch] = useDebounce(
    (searchVal?: string) => {
      search(searchVal);
    },
    500,
    [search]
  );

  const selectedLabel = useMemo(
    () =>
      value
        ? [...hiddenOptions, ...dropdownOptions].find((option) => String(option.value) === value)?.label
        : undefined,
    [dropdownOptions, hiddenOptions, value]
  );

  const options: DataTableSelectCellOption[] = useMemo(
    () =>
      counterparties.map((cp) => {
        const matchedHidden = getHighlightedSuffix(cp.search_metadata, HIDDEN_MATCH_CANDIDATES);
        return {
          value: cp.id,
          label: cp.name || cp.description || cp.wire?.beneficiary_name || 'Unnamed',
          description: matchedHidden ? (
            <span className="font-mono">
              <span className="text-muted-foreground/60">{matchedHidden.label} </span>
              {renderMatchFragment(matchedHidden.fragment)}
            </span>
          ) : cp.account_number || cp.routing_number ? (
            <span className="font-mono">
              {cp.account_number && (
                <>
                  <span className="text-muted-foreground/60">ACCT </span>
                  {highlightMatch(cp.account_number, query)}
                </>
              )}
              {cp.account_number && cp.routing_number && <span className="text-muted-foreground/60"> / </span>}
              {cp.routing_number && (
                <>
                  <span className="text-muted-foreground/60">RTN </span>
                  {highlightMatch(cp.routing_number, query)}
                </>
              )}
            </span>
          ) : undefined,
          'data-cy': `dropdown-option-${cp.id}`,
        };
      }),
    [counterparties, query]
  );

  return (
    <DataTable.SelectCell
      isEditable={isEditable}
      isSearchable
      options={options}
      value={value}
      displayValue={selectedLabel}
      searchPlaceholder="Search counterparties…"
      emptyMessage="No counterparties found"
      onSearch={(searchValue) => {
        setQuery(searchValue);
        handleSearch(searchValue);
      }}
      onScrollEnd={handleScrollEnd}
      onValueChange={(newValue) => {
        const cp = counterparties.find((counterparty) => counterparty.id === newValue);
        onChange?.({
          description: cp ? formatCounterpartyLabel(cp) : '',
          id: newValue,
        });
      }}
    />
  );
};
